import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  ListSubheader,
  Typography,
  createFilterOptions,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { entityKeys } from "../../../../api/queryKeys";
import SearchFilter from "../../../../components/filters/SearchFilter";
import { StyledTextField } from "../../../../components/forms/styles/formFieldStyles";
import LoadingIcon from "../../../../components/LoadingIcon";
import SvgToIcon from "../../../../icons/SvgToIcon";
import {
  isRawDataField,
  parseRawDataField,
} from "../../../stack/environment/entitiesTable/fieldUtils";
import { icons } from "../../../stack/environment/icons/iconExport";
import IntegrationImage from "../../../stack/integrations/components/IntegrationImage";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";
import IntegrationRowFilter from "../../steps/components/IntegrationRowFilter";

const formatFieldName = (fieldName) => {
  if (isRawDataField(fieldName)) {
    const { integration, field } = parseRawDataField(fieldName);
    const formattedField = field
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    const formattedIntegration = integration
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    return `${formattedField} (${formattedIntegration})`;
  }
  return fieldName
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getFieldKey = (field) => {
  return `${field.field_name}${
    field.source_integration ? `_${field.source_integration}` : ""
  }`;
};

const filterDuplicateFieldsByName = (fields) => {
  const uniqueFields = new Set();
  return fields.filter((field) => {
    const key = getFieldKey(field);
    if (uniqueFields.has(key)) {
      return false;
    }
    uniqueFields.add(key);
    return true;
  });
};

const EntityFieldMultiSelect = ({
  selectedFields = [],
  onSelectionChange,
  label = "Entity Fields",
  tenantIds = [],
}) => {
  const colors = useColors();
  const { getEntityFieldsForExport } = useAPI();
  const [sourceFilter, setSourceFilter] = useState({ source_integration: {} });

  const { data: fieldsData, isLoading: isLoadingFields } = useQuery({
    queryKey: entityKeys.fieldsForExport(tenantIds),
    queryFn: () => getEntityFieldsForExport(tenantIds),
    select: (response) => filterDuplicateFieldsByName(response.data),
  });

  const sourceOptions = useMemo(() => {
    const fields = fieldsData || [];
    const sources = fields.map((f) => f.source_integration).filter(Boolean);
    return [...new Set(sources)]
      .filter((source) => IntegrationsMapping[source])
      .sort()
      .map((source) => ({
        name: source,
        displayName: IntegrationsMapping[source].displayName,
        label: <IntegrationRowFilter name={source} />,
      }));
  }, [fieldsData]);

  const selectedSources = useMemo(() => {
    const filters = sourceFilter.source_integration;
    return Object.keys(filters).filter((key) => filters[key]);
  }, [sourceFilter]);

  const allFieldOptions = useMemo(() => {
    const fields = fieldsData || [];
    return fields
      .filter((field) => field && field.field_name)
      .map((field) => ({
        ...field,
        formattedName: formatFieldName(field.field_name),
        label: formatFieldName(field.field_name),
        value: field.field_name,
        group: field.source_integration
          ? field.source_integration
              .split("_")
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(" ")
          : "General",
      }));
  }, [fieldsData]);

  const fieldOptions = useMemo(() => {
    if (selectedSources.length === 0) {
      return allFieldOptions;
    }
    return allFieldOptions.filter((f) =>
      selectedSources.includes(f.source_integration)
    );
  }, [allFieldOptions, selectedSources]);

  const filterOptions = useMemo(
    () =>
      createFilterOptions({
        matchFrom: "any",
        stringify: (option) => {
          if (!option || typeof option !== "object") return "";
          const parts = [
            option.field_name,
            option.formattedName,
            option.label,
            option.group,
            option.source_integration,
          ]
            .filter(Boolean)
            .filter(
              (part) => typeof part === "string" && part.trim().length > 0
            );
          return parts.join(" ");
        },
        trim: true,
      }),
    []
  );

  const selectedOptions = useMemo(
    () => allFieldOptions.filter((opt) => selectedFields.includes(opt.value)),
    [allFieldOptions, selectedFields]
  );

  const shouldApplyDefaults =
    !isLoadingFields &&
    allFieldOptions.length > 0 &&
    selectedFields.length === 0;

  if (shouldApplyDefaults) {
    const defaultFields = allFieldOptions
      .filter((opt) => opt.default_selected)
      .map((opt) => opt.value);
    if (defaultFields.length > 0) {
      onSelectionChange(defaultFields);
    }
  }

  const handleChange = (event, newValue) => {
    onSelectionChange(newValue.map((opt) => opt.value));
  };

  if (isLoadingFields) {
    return (
      <Box>
        <Typography
          variant="caption"
          sx={{ color: colors.text_secondary, mb: 1, display: "block" }}
        >
          {label}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <LoadingIcon type="circular" size={16} />
          <Typography variant="caption">Loading fields...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="caption" sx={{ color: colors.text_secondary }}>
          {label}
        </Typography>
        {sourceOptions.length > 0 && (
          <SearchFilter
            filterName="Integration"
            fieldName="source_integration"
            options={sourceOptions}
            appliedFilters={sourceFilter}
            setAppliedFilters={setSourceFilter}
          />
        )}
      </Box>
      <Autocomplete
        multiple
        fullWidth
        size="small"
        disableCloseOnSelect
        limitTags={2}
        getLimitTagsText={(more) => `+${more}`}
        options={fieldOptions}
        filterOptions={filterOptions}
        groupBy={(option) => option.group}
        value={selectedOptions}
        onChange={handleChange}
        getOptionLabel={(option) => option.label || ""}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            placeholder={selectedOptions.length === 0 ? "Select fields" : ""}
            sx={{
              "& .MuiOutlinedInput-input": {
                fontSize: "14px",
                fontFamily: "Poppins, sans-serif",
                lineHeight: "14px",
                letterSpacing: "0.48px",
              },
            }}
          />
        )}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={getFieldKey(option)}>
            <Box display="flex" alignItems="center" gap={1}>
              <Checkbox
                checked={selected}
                sx={{ p: 0 }}
                icon={
                  <SvgToIcon
                    IconLogo={icons.Unchecked}
                    style={{ width: "20px", height: "20px" }}
                  />
                }
                checkedIcon={
                  <SvgToIcon
                    IconLogo={icons.Checked}
                    style={{ width: "20px", height: "20px" }}
                  />
                }
              />
              {option.source_integration && (
                <IntegrationImage
                  name={option.source_integration.toLowerCase()}
                  width={16}
                  newLogo={true}
                  showToolTip={false}
                />
              )}
              <Typography variant="caption" color={colors.text_primary}>
                {option.formattedName}
              </Typography>
            </Box>
          </li>
        )}
        renderGroup={(params) => (
          <li key={params.key}>
            <ListSubheader
              sx={{
                backgroundColor: colors.common_white,
                lineHeight: "32px",
              }}
            >
              <Typography variant="subtitle2" color={colors.text_secondary}>
                {params.group}
              </Typography>
            </ListSubheader>
            <ul style={{ padding: 0 }}>{params.children}</ul>
          </li>
        )}
        renderTags={(value, getTagProps) => {
          const maxTags = 2;
          const displayedTags = value.slice(0, maxTags);
          const extraCount = value.length - maxTags;

          return (
            <>
              {displayedTags.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={getFieldKey(option)}
                  label={option.formattedName}
                  size="small"
                  sx={{
                    height: "24px",
                    "& .MuiChip-label": {
                      fontSize: "12px",
                    },
                  }}
                />
              ))}
              {extraCount > 0 && (
                <Typography variant="caption" sx={{ ml: 0.5 }}>
                  +{extraCount}
                </Typography>
              )}
            </>
          );
        }}
        ListboxProps={{
          sx: {
            maxHeight: "300px",
            "& .MuiAutocomplete-option": {
              minHeight: "auto",
              padding: "8px 16px",
            },
            '& .MuiAutocomplete-option[aria-selected="true"]': {
              backgroundColor: "transparent",
            },
            '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            padding: "4px 9px",
            flexWrap: "nowrap !important",
            "& .MuiAutocomplete-input": {
              padding: "4px 4px",
              minWidth: "30px",
            },
            "& .MuiAutocomplete-tag": {
              maxWidth: "calc(50% - 6px)",
            },
          },
        }}
      />
    </Box>
  );
};

export default EntityFieldMultiSelect;
